import i18next from 'i18next';

import { PredictionPoint } from '../interfaces';
import { weightUnitsByCompany } from '../../../config/commons';
import { convertKilogramsToPounds } from '../../../helpers/stocking.helpers';
import { openErrorNotification } from '../../../common/notification/Notification';

export const getBiomass = (props: { biomass: number; weightUnit: string }) => {
  const { biomass, weightUnit } = props;
  switch (weightUnit) {
    case weightUnitsByCompany.KILOGRAM:
    default: {
      return Number(biomass);
    }

    case weightUnitsByCompany.POUND: {
      return convertKilogramsToPounds(Number(biomass));
    }
  }
};

export const showErrorThinning = (props: { error: string; }) => {
  const { error } = props;

  switch (error) {
    case 'invalidDay':
      openErrorNotification(i18next.t('thinnings.invalidDay'));
      break;
  }
};

export const getAverageWeight = (props: {day: number; allPredictions: PredictionPoint[]; }) => {
  const { day, allPredictions } = props;

  if (!day) {
    return 0;
  }

  const prediction = allPredictions.find((prediction) => prediction.x === Number(day));
  if (!prediction) {
    return 0;
  }

  return prediction.y;
};

export const getIncome = (props: {day: number; allPredictions: PredictionPoint[]; packerId: string; }) => {
  const { day, allPredictions, packerId } = props;

  if (!day || !packerId) {
    return { prediction: undefined, commercialSize: undefined };
  }

  const prediction = allPredictions.find((prediction) => prediction.x === Number(day));
  if (!prediction) {
    return { prediction: undefined, commercialSize: undefined };
  }

  const commercialSize = prediction.commercialSizeData.find((commercialSize) => commercialSize.packerId === packerId);
  return { prediction, commercialSize };
};

export const getTailIncome = (props: {day: number; allPredictions: PredictionPoint[]; packerId: string; biomassKg: number; }) => {
  const { day, allPredictions, packerId, biomassKg } = props;

  const { prediction, commercialSize } = getIncome({ day, allPredictions, packerId });
  if (!prediction || !prediction.biomass || !commercialSize) {
    return 0;
  }

  const income = commercialSize.histogram.income.tail || 0;
  const value = (biomassKg * income) / prediction.biomass;
  return value;
};

export const getWholeIncome = (props: {day: number; allPredictions: PredictionPoint[]; packerId: string; biomassKg: number; }) => {
  const { day, allPredictions, packerId, biomassKg } = props;

  const { prediction, commercialSize } = getIncome({ day, allPredictions, packerId });
  if (!prediction || !prediction.biomass || !commercialSize) {
    return 0;
  }

  const income = commercialSize.histogram.income.whole || 0;
  const value = (biomassKg * income) / prediction.biomass;
  return value;
};

export const getWasteIncome = (props: {day: number; allPredictions: PredictionPoint[]; packerId: string; biomassKg: number; }) => {
  const { day, allPredictions, packerId, biomassKg } = props;

  const { prediction, commercialSize } = getIncome({ day, allPredictions, packerId });
  if (!prediction || !prediction.biomass || !commercialSize) {
    return 0;
  }

  const income = commercialSize.histogram.income.waste || 0;
  const value = (biomassKg * income) / prediction.biomass;
  return value;
};

export const getAnimals = (props: {day: number; allPredictions: PredictionPoint[]; biomassKg: number; }) => {
  const { day, allPredictions, biomassKg } = props;

  if (!day || !biomassKg) {
    return 0;
  }

  const prediction = allPredictions.find((prediction) => prediction.x === Number(day));
  if (!prediction) {
    return 0;
  }

  return Math.round((biomassKg * 1000) / prediction.y);
};

export const getMaxBiomass = (props: {day: number; allPredictions: PredictionPoint[]; weightUnit: string;}) => {
  const { day, allPredictions, weightUnit } = props;

  if (!day) {
    return 0;
  }

  const prediction = allPredictions.find((prediction) => prediction.x === Number(day));
  if (!prediction) {
    return 0;
  }

  switch (weightUnit) {
    case weightUnitsByCompany.KILOGRAM:
    default: {
      return prediction.biomass;
    }

    case weightUnitsByCompany.POUND: {
      return prediction.biomassLb;
    }
  }
};

export const isValidAmountBiomass = (props: {day: number; biomassKg: number; allPredictions: PredictionPoint[]; }) => {
  const { day, biomassKg, allPredictions } = props;

  if (!day) {
    return false;
  }

  const prediction = allPredictions.find((prediction) => prediction.x === Number(day));
  if (!prediction) {
    return false;
  }

  return prediction.biomass >= biomassKg;
};

export const disableNewThinningForm = (props: {day: number; biomassKg: number; packerId: string; allPredictions: PredictionPoint[]; }) => {
  const { day, biomassKg, packerId, allPredictions } = props;
  return !day || !biomassKg || !packerId || !isValidAmountBiomass({ day, biomassKg, allPredictions });
};
